import { useState, useCallback, RefObject } from 'react';
import { CAROUSEL_CONFIG } from '../../constants/suggestionWizard';

interface UseCarouselProps {
  containerRef: RefObject<HTMLDivElement>;
  totalItems: number;
  cardWidth?: number;
  gap?: number;
}

interface UseCarouselReturn {
  activeIndex: number;
  scrollToIndex: (index: number) => void;
  handleScroll: () => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

/**
 * Hook for managing carousel navigation and scroll state
 */
export const useCarousel = ({
  containerRef,
  totalItems,
  cardWidth = CAROUSEL_CONFIG.CARD_WIDTH,
  gap = CAROUSEL_CONFIG.GAP,
}: UseCarouselProps): UseCarouselReturn => {
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = useCallback((index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const clampedIndex = Math.max(0, Math.min(index, totalItems - 1));
    const scrollPosition = clampedIndex * (cardWidth + gap);

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
    setActiveIndex(clampedIndex);
  }, [containerRef, totalItems, cardWidth, gap]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const newIndex = Math.round(scrollLeft / (cardWidth + gap));
    setActiveIndex(newIndex);
  }, [containerRef, cardWidth, gap]);

  const canScrollLeft = activeIndex > 0;
  const canScrollRight = activeIndex < totalItems - 1;

  return {
    activeIndex,
    scrollToIndex,
    handleScroll,
    canScrollLeft,
    canScrollRight,
  };
};

export default useCarousel;
